import * as React from 'react'

import { MapRef } from 'react-map-gl/mapbox'

const isSourceLoading = (gl: mapboxgl.Map, sourceId: string): boolean =>
  !!gl.getSource(sourceId) && !gl.isSourceLoaded(sourceId)

// mapbox-gl 3.32 leaves tiles on the OLD data when setData lands while they are still loading;
// sending the same data again once the source has loaded rebuilds them.
export function useResendDataAfterLoad(
  map: MapRef | undefined,
  sourceId: string,
  data: GeoJSON.GeoJSON,
): void {
  const gl = map?.getMap()
  const renderedData = React.useRef(data)
  const landedMidLoad = React.useRef(false)
  // Read during render: the Source child hands the map this data as it renders, after this.
  if (renderedData.current !== data) {
    renderedData.current = data
    landedMidLoad.current ||= !!gl && isSourceLoading(gl, sourceId)
  }

  React.useEffect(() => {
    if (!gl || !landedMidLoad.current) return undefined

    const onRender = (): void => {
      if (isSourceLoading(gl, sourceId)) return
      gl.off('render', onRender)
      landedMidLoad.current = false
      const source = gl.getSource(sourceId) as mapboxgl.GeoJSONSource
      source?.setData(data)
    }
    gl.on('render', onRender)
    // The load may have finished before this subscribed; a frame checks it either way.
    gl.triggerRepaint()
    return () => {
      gl.off('render', onRender)
    }
  }, [gl, sourceId, data])
}
